import {
  UserProfile,
  WalletBalance,
  AllowedInstrument,
  Candle,
  LockedQuote,
  Contract,
  Timeframe,
} from "@starx/shared-types";

export class RestClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = "") {
    this.baseURL = baseURL;
  }

  public setToken(token: string | null) {
    this.token = token;
  }

  public getToken(): string | null {
    return this.token;
  }

  public getDisplayCurrency(base: string, amount: string, target = '', country = ''): Promise<any> {
    const tz = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
    const params: Record<string, string> = { base, amount, target };
    if (tz) params.tz = tz;
    if (country) params.country = country;
    return this.request('/api/v1/account/display-currency?' + new URLSearchParams(params));
  }
  public getCurrencyPolicy(currency: string): Promise<any> {
    return this.request('/api/v1/account/currency-policy?' + new URLSearchParams({currency}));
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    const effectiveToken = this.token || (typeof localStorage !== 'undefined' ? (localStorage.getItem('starx_auth_token') || localStorage.getItem('starx_session_token')) : null);
    if (effectiveToken && typeof effectiveToken === 'string' && effectiveToken.trim()) {
      const cleanToken = effectiveToken.trim().replace(/^Bearer\s+/i, '');
      headers["Authorization"] = `Bearer ${cleanToken}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    if (!response.ok) {
      const errMessage =
        data?.error?.message || data?.message || (typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`);
      const errCode = data?.code || data?.error?.code || (typeof data?.error === 'string' ? data.error : "API_ERROR");
      const error = new Error(errMessage) as any;
      error.code = errCode;
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data as T;
  }

  public async getDevToken(
    userId: string = "00000000-0000-0000-0000-000000000002",
    operatorId: string = "00000000-0000-0000-0000-000000000001",
  ): Promise<{ token: string; user_id: string; role: string }> {
    return this.request("/api/v1/auth/dev-token", {
      method: "POST",
      headers: {
        "X-Operator-ID": operatorId,
      },
      body: JSON.stringify({
        user_id: userId,
        operator_id: operatorId,
        role: "TRADER",
      }),
    });
  }

  public async getWSTicket(): Promise<{
    ws_ticket: string;
    expires_in: number;
  }> {
    return this.request("/api/v1/auth/ws-ticket", { method: "POST" });
  }

  public async getProfile(): Promise<UserProfile> {
    return this.request("/api/v1/account/profile");
  }

  public async updateAccountCountry(countryCode: string): Promise<{ country_code: string }> {
    return this.request("/api/v1/account/profile/country", {
      method: "POST",
      body: JSON.stringify({ country_code: countryCode }),
    });
  }

  public async updateAccountName(name: string): Promise<{
    user_id: string;
    full_name: string;
    public_alias: string;
    name_changed_at: string;
    can_change_name: boolean;
    days_until_name_change: number;
  }> {
    return this.request("/api/v1/account/profile/name", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  public async getAvailableBonuses(): Promise<any> {
    return this.request("/api/v1/bonuses/available");
  }

  public async claimBonus(campaignId: string, depositId?: string): Promise<any> {
    return this.request("/api/v1/bonuses/claim", {
      method: "POST",
      body: JSON.stringify({ campaign_id: campaignId, deposit_id: depositId || undefined }),
    });
  }

  public async getBonusProgress(): Promise<any> {
    return this.request("/api/v1/bonuses/progress");
  }

  public async getWalletSummary(currency?: string): Promise<any> {
    const q = currency ? `?currency=${encodeURIComponent(currency)}` : "";
    return this.request(`/api/v1/wallet/summary${q}`);
  }

  public async cancelBonus(grantId?: string): Promise<any> {
    return this.request("/api/v1/bonuses/cancel", {
      method: "POST",
      body: JSON.stringify({ grant_id: grantId || "" }),
    });
  }

  public async getChartLayouts(): Promise<any> {
    return this.request("/api/v1/chart/layouts");
  }

  public async saveChartLayout(layout: Record<string, unknown>): Promise<any> {
    return this.request("/api/v1/chart/layouts", { method: "PUT", body: JSON.stringify(layout) });
  }

  public async listPendingOrders(): Promise<any> {
    return this.request("/api/v1/pending-orders");
  }

  public async createPendingOrder(order: Record<string, unknown>): Promise<any> {
    return this.request("/api/v1/pending-orders", { method: "POST", body: JSON.stringify(order) });
  }

  public async updatePendingOrder(orderId: string, status: "TRIGGERED" | "FAILED", failureReason?: string): Promise<any> {
    return this.request("/api/v1/pending-orders", { method: "PATCH", body: JSON.stringify({ order_id: orderId, status, failure_reason: failureReason }) });
  }

  public async cancelPendingOrder(orderId: string): Promise<any> {
    return this.request(`/api/v1/pending-orders?id=${encodeURIComponent(orderId)}`, { method: "DELETE" });
  }

  public async beginLogin(): Promise<{ authorization_url: string }> {
    return this.request("/api/v1/auth/login");
  }

  public async getBalance(currency: string = "USD"): Promise<WalletBalance> {
    return this.request(
      `/api/v1/account/balance?currency=${encodeURIComponent(currency)}`,
    );
  }

  public async getDemoBalance(currency: string = "USD"): Promise<WalletBalance> {
    return this.request(
      `/api/v1/account/balance?mode=DEMO&currency=${encodeURIComponent(currency)}`,
    );
  }

  public async syncDemoBalance(balance: number): Promise<any> {
    return this.request("/api/v1/account/demo-balance", {
      method: "POST",
      body: JSON.stringify({ balance }),
    });
  }

  public async getInstruments(): Promise<AllowedInstrument[]> {
    return this.request("/api/v1/instruments");
  }

  public async getCandles(
    symbol: string,
    timeframe: Timeframe = "1m",
    limit: number = 200,
  ): Promise<{ candles: Candle[] }> {
    return this.request(
      `/api/v1/market/candles?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}&limit=${limit}`,
    );
  }

  public async requestQuote(
    symbol: string,
    direction: "CALL" | "PUT",
    durationSeconds: number,
    currency: string = "USD",
    turnstileToken?: string,
    stake?: string,
  ): Promise<LockedQuote> {
    const headers: Record<string, string> = {};
    if (turnstileToken) {
      headers["cf-turnstile-response"] = turnstileToken;
      headers["X-Turnstile-Token"] = turnstileToken;
    }
    return this.request("/api/v1/quotes/request", {
      method: "POST",
      headers,
      body: JSON.stringify({
        symbol,
        direction,
        duration_seconds: durationSeconds,
        currency,
        stake,
      }),
    });
  }

  public async openContract(
    idempotencyKey: string,
    contractId: string,
    quoteId: string,
    stake: string,
    turnstileToken?: string,
    riskFree?: { is_risk_free?: boolean; risk_free_token_id?: string; risk_free_max_refund?: number },
  ): Promise<{ success: boolean; contract_id: string; tx_id: string }> {
    const headers: Record<string, string> = {};
    if (turnstileToken) {
      headers["cf-turnstile-response"] = turnstileToken;
      headers["X-Turnstile-Token"] = turnstileToken;
    }
    return this.request("/api/v1/contracts/open", {
      method: "POST",
      headers,
      body: JSON.stringify({
        idempotency_key: idempotencyKey,
        contract_id: contractId,
        quote_id: quoteId,
        stake,
        is_risk_free: riskFree?.is_risk_free || false,
        risk_free_token_id: riskFree?.risk_free_token_id || "",
        risk_free_max_refund: riskFree?.risk_free_max_refund || 0,
      }),
    });
  }

  public async executeDirectTrade(params: {
    symbol: string;
    direction: "CALL" | "PUT";
    duration_seconds: number;
    stake: string;
    currency: string;
    contract_id?: string;
    idempotency_key?: string;
    strike_price?: number;
    temp_optimistic_id?: string;
    is_risk_free?: boolean;
    risk_free_token_id?: string;
    risk_free_max_refund?: number;
    turnstile_token?: string;
  }): Promise<{ success: boolean; contract: Contract; temp_optimistic_id?: string }> {
    const headers: Record<string, string> = {};
    if (params.turnstile_token) {
      headers["cf-turnstile-response"] = params.turnstile_token;
      headers["X-Turnstile-Token"] = params.turnstile_token;
    }
    return this.request("/api/v1/trade/execute", {
      method: "POST",
      headers,
      body: JSON.stringify(params),
    });
  }

  public async getActiveContracts(): Promise<Contract[]> {
    const response: any = await this.request("/api/v1/contracts/active");
    return Array.isArray(response) ? response : response?.contracts || [];
  }

  public async getContractHistory(limit?: number): Promise<Contract[]> {
    return this.request(
      limit
        ? `/api/v1/contracts/history?limit=${limit}`
        : "/api/v1/contracts/history",
    );
  }

  public async getContract(contractId: string): Promise<Contract> {
    return this.request(`/api/v1/contracts/${encodeURIComponent(contractId)}`);
  }

  public async getInstrumentSession(symbol: string): Promise<{
    symbol: string;
    is_otc: boolean;
    is_open: boolean;
    session_type: string;
    status_label?: string;
    feed_status?: 'LIVE' | 'MARKET_CLOSED' | 'OTC_CLOSED' | 'FEED_DELAYED' | 'FEED_UNAVAILABLE';
    feed_is_fresh?: boolean;
    trading_available?: boolean;
    feed_staleness_ms?: number;
    last_tick_at?: string;
  }> {
    return this.request(
      `/api/v1/instruments/session?symbol=${encodeURIComponent(symbol)}`,
    );
  }

  public async getPayoutScheduleQuote(
    symbol: string,
    duration: number,
    stakeCents: number = 10000,
    direction: string = "",
    currency: string = "USD",
  ): Promise<any> {
    const dirParam = direction ? `&direction=${encodeURIComponent(direction)}` : "";
    return this.request(
      `/api/v1/payout-schedules/quote?symbol=${encodeURIComponent(symbol)}&duration=${duration}&stake_cents=${stakeCents}&currency=${encodeURIComponent(currency)}${dirParam}`,
    );
  }

  public async getPayoutBatch(symbols: string[], duration: number, stake: string, currency: string): Promise<any> {
    const query = new URLSearchParams({symbols: symbols.join(","), duration: String(duration), stake, currency});
    return this.request(`/api/v1/payout-schedules/quote?${query}`);
  }

  public async quoteEarlyCashout(contractId: string): Promise<{
    quote_id: string;
    contract_id: string;
    quote_payout: string;
    quote_payout_cents: number;
    haircut_bps: number;
    expires_at: string;
  }> {
    return this.request("/api/v1/trade/cashout/quote", {
      method: "POST",
      body: JSON.stringify({ contract_id: contractId }),
    });
  }

  public async executeEarlyCashout(
    quoteId: string,
    idempotencyKey: string,
  ): Promise<{
    contract_id: string;
    status: string;
    cashout_payout_cents: number;
    cashout_payout: number;
    payout?: string;
    journal_tx_id: string;
  }> {
    return this.request("/api/v1/trade/cashout/execute", {
      method: "POST",
      body: JSON.stringify({
        quote_id: quoteId,
        idempotency_key: idempotencyKey,
      }),
    });
  }

  public async getLedgerStatement(
    limit: number = 50,
    cursor?: string,
    currency?: string,
  ): Promise<{
    items: any[];
    cursor?: string;
    limit: number;
    currency: string;
    total_count: number;
  }> {
    let url = `/api/v1/account/ledger?limit=${limit}`;
    if (currency) {
      url += `&currency=${encodeURIComponent(currency)}`;
    }
    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }
    return this.request(url);
  }

  public async createDepositIntent(
    paymentMethod: string,
    amount: string,
    currency: string = "USDT",
    bonusCampaignId?: string,
    targetCurrency?: string,
    targetAmount?: string,
    fxRate?: string,
  ): Promise<any> {
    return this.request("/api/v1/cashier/deposit/intent", {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: `IDEM-WEB-DEP-${Date.now()}-${crypto.randomUUID()}`,
        currency,
        amount,
        payment_method: paymentMethod,
        bonus_campaign_id: bonusCampaignId || undefined,
        target_currency: targetCurrency || undefined,
        target_amount: targetAmount || undefined,
        fx_rate: fxRate || undefined,
      }),
    });
  }

  public async submitDepositProof(
    depositId: string,
    transactionHash: string,
  ): Promise<any> {
    return this.request("/api/v1/cashier/deposit/proof", {
      method: "POST",
      body: JSON.stringify({
        deposit_id: depositId,
        transaction_hash: transactionHash,
      }),
    });
  }

  public async getCashierTransactions(): Promise<any> {
    return this.request("/api/v1/cashier/transactions");
  }

  public async requestWithdrawal(
    destinationNetwork: string,
    amount: string,
    destinationAddress: string,
    currency: string = "USDT",
  ): Promise<any> {
    return this.request("/api/v1/cashier/withdraw", {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: `IDEM-WEB-WDR-${Date.now()}-${crypto.randomUUID()}`,
        currency,
        amount,
        fee: "0.00",
        destination_address: destinationAddress,
        destination_network: destinationNetwork,
      }),
    });
  }

  public async getLeaderboard(
    timeframe: "daily" | "weekly" | "all_time" = "weekly",
  ): Promise<any> {
    return this.request(
      `/api/v1/leaderboard?timeframe=${encodeURIComponent(timeframe)}`,
    );
  }

  public async getTournaments(): Promise<any> {
    return this.request("/api/v1/tournaments");
  }

  public async joinTournament(tournamentId: string): Promise<any> {
    return this.request("/api/v1/tournaments", {
      method: "POST",
      body: JSON.stringify({ tournament_id: tournamentId }),
    });
  }

  public async getTerminalConfig(): Promise<any> {
    return this.request("/api/v1/terminal/config");
  }

  public async getChartCapabilities(): Promise<any> {
    return this.request("/api/v1/chart/capabilities");
  }

  public async getTerminalPreferences(): Promise<any> {
    return this.request("/api/v1/terminal/preferences");
  }

  public async saveTerminalPreferences(
    preferences: Record<string, unknown>,
  ): Promise<any> {
    return this.request("/api/v1/terminal/preferences", {
      method: "PUT",
      body: JSON.stringify({ preferences }),
    });
  }

  public async listSupportTickets(): Promise<any> {
    return this.request("/api/v1/support/tickets");
  }

  public async createSupportTicket(payload: {
    category: string;
    priority: string;
    subject: string;
    message: string;
    email?: string;
  }): Promise<any> {
    return this.request("/api/v1/support/tickets", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  public async getTenantSettings(
    section: "PAYMENT_METHOD" | "SOCIAL" | "PAYOUT_POLICY" | "TOURNAMENT" | "TERMINAL_FEATURE",
  ): Promise<any> {
    return this.request(`/api/v1/account/tenant-settings/${section}`);
  }

  public async getResponsibleTradingControls(): Promise<any> {
    return this.request("/api/v1/account/responsible-trading");
  }

  public async updateResponsibleTradingControl(
    action: "SET_DAILY_LOSS_LIMIT" | "START_COOLING_OFF" | "SELF_EXCLUDE",
    value: string,
  ): Promise<any> {
    return this.request("/api/v1/account/responsible-trading", {
      method: "POST",
      body: JSON.stringify({
        action,
        value,
        idempotency_key: `IDEM-WEB-RESP-${Date.now()}-${crypto.randomUUID()}`,
      }),
    });
  }

  public async listKycDocuments(): Promise<{ items: any[]; total_count: number }> {
    return this.request("/api/v1/account/kyc/documents");
  }

  public async submitKycDocument(payload: {
    document_type: string;
    storage_provider?: string;
    object_key?: string;
    content_type?: string;
    sha256?: string;
    id_number?: string;
    document_url?: string;
  }): Promise<any> {
    return this.request("/api/v1/account/kyc/documents", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  public async getGemsMarket(): Promise<any> {
    return this.request("/api/v1/gems/market");
  }

  public async buyGemsPerk(itemId: string): Promise<any> {
    return this.request("/api/v1/gems/buy-perk", {
      method: "POST",
      body: JSON.stringify({ item_id: itemId }),
    });
  }

  public async consumeGemsPerk(tokenId: string): Promise<any> {
    return this.request("/api/v1/gems/consume-token", {
      method: "POST",
      body: JSON.stringify({ token_id: tokenId }),
    }).catch(() => null);
  }

  public async claimDailyStreak(): Promise<any> {
    return this.request("/api/v1/gems/claim-daily-streak", {
      method: "POST",
    });
  }

  public async getNotifications(limit: number = 50): Promise<{ notifications: any[] }> {
    return this.request(`/api/v1/notifications?limit=${limit}`);
  }

  public async getCryptoRates(): Promise<{ rates: Record<string, number>; usd_inr: number; updated_at: string }> {
    return this.request("/api/v1/cashier/crypto-rates");
  }
}
